import { useEffect, useState, useMemo } from "react";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Fuel, Settings as SettingsIcon, TrendingUp, TrendingDown, Users2, X, Save, Info, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BAND_STYLE = {
  red:    { bar: "bg-red-500",     text: "text-red-300",     ring: "border-red-500/50",     glow: "shadow-red-500/20" },
  orange: { bar: "bg-orange-500",  text: "text-orange-300",  ring: "border-orange-500/50", glow: "shadow-orange-500/20" },
  yellow: { bar: "bg-yellow-400",  text: "text-yellow-300",  ring: "border-yellow-500/50", glow: "shadow-yellow-500/20" },
  green:  { bar: "bg-emerald-500", text: "text-emerald-300", ring: "border-emerald-500/50", glow: "shadow-emerald-500/20" },
  unset:  { bar: "bg-slate-700",   text: "text-slate-400",   ring: "border-slate-700",     glow: "" },
};

function Delta({ n }) {
  if (n === 0) return <span className="text-slate-500 text-[10px]">no change</span>;
  const up = n > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${up ? "text-emerald-400" : "text-red-400"}`}>
      <Icon className="w-3 h-3"/> {up ? "+" : ""}{n}
    </span>
  );
}

function Sparkline({ labels, counts, peak }) {
  const w = 260, h = 90, pad = 8;
  const max = Math.max(peak || 0, ...counts, 1);
  const step = counts.length > 1 ? (w - pad * 2) / (counts.length - 1) : 0;
  const pts = counts.map((c, i) => {
    const x = pad + step * i;
    const y = h - pad - ((c / max) * (h - pad * 2));
    return `${x},${y}`;
  }).join(" ");
  const areaPts = `${pad},${h - pad} ${pts} ${pad + step * (counts.length - 1)},${h - pad}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" data-testid="fuel-sparkline">
      <defs>
        <linearGradient id="fuelArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff8c00" stopOpacity="0.5"/>
          <stop offset="100%" stopColor="#ff8c00" stopOpacity="0.02"/>
        </linearGradient>
      </defs>
      <polygon points={areaPts} fill="url(#fuelArea)"/>
      <polyline points={pts} fill="none" stroke="#ff8c00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      {counts.map((c, i) => {
        const x = pad + step * i;
        const y = h - pad - ((c / max) * (h - pad * 2));
        const isPeak = c === Math.max(...counts) && c > 0;
        return (
          <g key={i}>
            <circle cx={x} cy={y} r={isPeak ? 3.5 : 2} fill={isPeak ? "#ff8c00" : "#f59e0b"}/>
            {isPeak && <text x={x} y={y - 6} textAnchor="middle" fontSize="9" fill="#ffb060" fontWeight="700">{c}</text>}
          </g>
        );
      })}
    </svg>
  );
}

function ConfigDialog({ open, onClose, config, onSaved }) {
  const [ratio, setRatio] = useState(String(config?.conversion_ratio ?? 10));
  const [days, setDays] = useState(String(config?.working_days ?? 26));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setRatio(String(config?.conversion_ratio ?? 10));
      setDays(String(config?.working_days ?? 26));
    }
  }, [open, config]);

  const ratioN = parseInt(ratio || "0", 10);
  const daysN = parseInt(days || "0", 10);
  const totalTarget = config?.total_target_cases || 0;
  const monthly = totalTarget * (ratioN || 0);
  const daily = daysN > 0 ? Math.ceil(monthly / daysN) : 0;
  const activeTC = config?.active_telecallers || 0;
  const valid = ratioN >= 1 && ratioN <= 50 && daysN >= 1 && daysN <= 31;

  const save = async () => {
    if (!valid) return toast.error("Enter valid values");
    setSaving(true);
    try {
      await api.put("/settings/conversion-ratio", { conversion_ratio: ratioN, working_days: daysN });
      toast.success("Business config saved");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="bg-slate-900 border-orange-500/40 text-white max-w-md" data-testid="fuel-config-dialog">
        <DialogHeader>
          <DialogTitle className="text-orange-400 flex items-center gap-2">
            <SettingsIcon className="w-4 h-4"/> Business Conversion Settings
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-400">
              How many leads needed for 1 closing? <span className="text-orange-400">*</span>
            </Label>
            <Input
              data-testid="fuel-config-ratio"
              type="number" min={1} max={50}
              value={ratio}
              onChange={e => setRatio(e.target.value)}
              className="bg-slate-950 border-slate-700 mt-1"
            />
            <div className="text-[10px] text-slate-500 mt-1">Between 1 and 50 · default 10</div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-400">
              Working days per month <span className="text-orange-400">*</span>
            </Label>
            <Input
              data-testid="fuel-config-days"
              type="number" min={1} max={31}
              value={days}
              onChange={e => setDays(e.target.value)}
              className="bg-slate-950 border-slate-700 mt-1"
            />
            <div className="text-[10px] text-slate-500 mt-1">Between 1 and 31 · default 26</div>
          </div>
          <div className="rounded-md bg-orange-500/10 border border-orange-500/30 p-3 text-[11px] text-orange-100 leading-relaxed" data-testid="fuel-config-preview">
            <div className="flex items-start gap-2">
              <Info className="w-3.5 h-3.5 text-orange-400 mt-0.5 shrink-0"/>
              <div>
                Current: <b>{activeTC} Telecaller{activeTC === 1 ? "" : "s"}</b> · target sum <b>{totalTarget} cases</b>.<br/>
                {totalTarget} × {ratioN || "?"} = <b>{monthly}</b> leads needed per month.<br/>
                Daily = {monthly}/{daysN || "?"} = <b>{daily}</b> leads.
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose} disabled={saving} className="text-slate-300"><X className="w-4 h-4 mr-2"/> Cancel</Button>
          <Button
            data-testid="fuel-config-save"
            onClick={save}
            disabled={!valid || saving}
            className="bg-orange-500 hover:bg-orange-400 text-slate-950 font-bold rounded-full disabled:opacity-40"
          >
            <Save className="w-4 h-4 mr-2"/> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DailyFuelMeter() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cfgOpen, setCfgOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/leads/fuel-stats");
      setStats(r.data);
    } catch (e) { /* silent — non-critical widget */ }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 180000); // 3 minutes
    return () => clearInterval(t);
  }, []);

  const band = stats?.status?.band || "unset";
  const style = BAND_STYLE[band] || BAND_STYLE.unset;
  const dailyPct = Math.min(100, stats?.percentages?.daily || 0);
  const dailyPctRaw = stats?.percentages?.daily || 0;

  const cards = useMemo(() => stats ? [
    { key: "today",     label: "Today",      value: stats.today,     delta: stats.delta.today_vs_yesterday,     subtitle: "vs yesterday" },
    { key: "yesterday", label: "Yesterday",  value: stats.yesterday, delta: stats.delta.yesterday_vs_prev,      subtitle: "vs previous day" },
    { key: "week",      label: "This Week",  value: stats.week,      delta: stats.delta.week_vs_prev,           subtitle: "vs last week" },
    { key: "month",     label: "This Month", value: stats.month,     delta: stats.delta.month_vs_prev,          subtitle: "vs last month" },
  ] : [], [stats]);

  if (loading && !stats) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 mb-4 text-center text-slate-500 text-sm" data-testid="fuel-meter-loading">
        Loading fuel meter…
      </div>
    );
  }
  if (!stats) return null;

  return (
    <div className="mb-4 space-y-3" data-testid="fuel-meter">
      {/* Row 1: Big fuel gauge + 7-day trend */}
      <div className="grid gap-3 lg:grid-cols-3">
        <div className={`lg:col-span-2 rounded-xl border-2 ${style.ring} bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-5 shadow-lg ${style.glow}`} data-testid="fuel-gauge">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className={`w-11 h-11 rounded-xl bg-orange-500/20 border border-orange-500/40 flex items-center justify-center shrink-0`}>
                <Fuel className="w-5 h-5 text-orange-400"/>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">⛽ Today's Fuel · Daily Leads Meter</div>
                <div className="font-heading text-2xl md:text-3xl text-white mt-0.5">
                  <span data-testid="fuel-today-count">{stats.today}</span>
                  <span className="text-slate-500 text-lg font-normal ml-1">
                    / <span data-testid="fuel-daily-needed">{stats.config.daily_needed}</span> leads today
                  </span>
                </div>
                <div className={`text-[13px] font-bold mt-1 ${style.text}`} data-testid="fuel-status-message">
                  {stats.status.message}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={load}
                title="Refresh fuel stats"
                data-testid="fuel-refresh-btn"
                className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center justify-center text-slate-400"
              ><RefreshCw className="w-3.5 h-3.5"/></button>
              <button
                type="button"
                onClick={() => setCfgOpen(true)}
                title="Business Conversion Settings"
                data-testid="fuel-settings-btn"
                className="w-8 h-8 rounded-full bg-orange-500/15 hover:bg-orange-500/25 border border-orange-500/40 flex items-center justify-center text-orange-300"
              ><SettingsIcon className="w-3.5 h-3.5"/></button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 mb-1">
              <span>Progress</span>
              <span>
                <span className="text-white font-mono-alt" data-testid="fuel-daily-pct">{dailyPctRaw}%</span>
                {" · "}Yesterday <span className="text-slate-300 font-mono-alt">{stats.yesterday}</span>
              </span>
            </div>
            <div className="h-3 rounded-full bg-slate-800 overflow-hidden border border-slate-700">
              <div
                className={`h-full ${style.bar} transition-all duration-700`}
                style={{ width: `${dailyPct}%` }}
                data-testid="fuel-progress-bar"
              />
            </div>
            <div className="mt-2 flex justify-between text-[10px] text-slate-500">
              <span>Month: <span className="text-white font-mono-alt">{stats.month}</span> / {stats.config.monthly_needed}</span>
              <span>{stats.config.month_label} · <span className="text-orange-300">{stats.percentages.monthly}%</span></span>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4" data-testid="fuel-trend-card">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">7-Day Trend</div>
            <Badge className="bg-orange-500/15 text-orange-300 border-orange-500/40 text-[9px]">
              PEAK {stats.trend.peak}
            </Badge>
          </div>
          <Sparkline labels={stats.trend.labels} counts={stats.trend.counts} peak={stats.trend.peak}/>
          <div className="flex justify-between text-[9px] text-slate-500 px-1">
            {stats.trend.labels.map((l, i) => (
              <span key={i} className={i === stats.trend.labels.length - 1 ? "text-orange-300 font-bold" : ""}>{l.split(" ")[0]}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Row 2: 4 stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map(c => (
          <div key={c.key} className="rounded-xl border border-slate-800 bg-slate-900/60 p-3 hover:border-orange-500/40 transition-colors" data-testid={`fuel-card-${c.key}`}>
            <div className="flex items-center gap-1.5">
              <Fuel className="w-3 h-3 text-orange-400"/>
              <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">{c.label}</div>
            </div>
            <div className="font-display text-2xl text-white mt-1" data-testid={`fuel-card-${c.key}-value`}>{c.value}</div>
            <div className="mt-1 flex items-center gap-1">
              <Delta n={c.delta}/>
              <span className="text-[9px] text-slate-500">· {c.subtitle}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Staff breakdown */}
      {stats.staff_breakdown && stats.staff_breakdown.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3" data-testid="fuel-staff-breakdown">
          <div className="flex items-center gap-2 mb-2">
            <Users2 className="w-3.5 h-3.5 text-orange-400"/>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
              Telecaller Fuel Breakdown · {stats.config.month_label}
            </div>
            <div className="ml-auto text-[10px] text-slate-500">
              ratio <span className="text-orange-300 font-mono-alt">{stats.config.conversion_ratio}</span> · working days <span className="text-orange-300 font-mono-alt">{stats.config.working_days}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[680px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="text-left py-1.5 pl-1">Staff</th>
                  <th className="text-right py-1.5">Target Cases</th>
                  <th className="text-right py-1.5">Leads Needed</th>
                  <th className="text-right py-1.5">Leads Got</th>
                  <th className="text-right py-1.5 pr-1">%</th>
                </tr>
              </thead>
              <tbody>
                {stats.staff_breakdown.map(s => {
                  const p = s.pct;
                  const dot = p >= 91 ? "bg-emerald-400" : p >= 61 ? "bg-yellow-400" : p >= 31 ? "bg-orange-400" : "bg-red-500";
                  return (
                    <tr key={s.staff_id} className="border-t border-slate-800" data-testid={`fuel-staff-row-${s.staff_id}`}>
                      <td className="py-1.5 pl-1">
                        <div className="text-white font-semibold">{s.name}</div>
                        <div className="text-[9px] text-slate-500 font-mono-alt">{s.phone} · {s.district || "—"}</div>
                      </td>
                      <td className="py-1.5 text-right font-mono-alt text-slate-200">{s.target_cases}</td>
                      <td className="py-1.5 text-right font-mono-alt text-orange-300">{s.leads_needed}</td>
                      <td className="py-1.5 text-right font-mono-alt text-white">{s.leads_got}</td>
                      <td className="py-1.5 text-right pr-1">
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`inline-block w-2 h-2 rounded-full ${dot}`}/>
                          <span className="font-mono-alt text-slate-200">{p}%</span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfigDialog open={cfgOpen} onClose={() => setCfgOpen(false)} config={stats.config} onSaved={load}/>
    </div>
  );
}
